import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, X } from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import { getWebIDsByUser, updateEmergencyProfile } from '../../services/webids';
import { WebIDData } from '../../services/webids';
import EmergencyProfileFormUnified, { 
  EmergencyProfileFormData,
  EmergencyContact 
} from '../ui/EmergencyProfileFormUnified';
import { Button } from '../ui';
import LoadingSpinner from '../ui/LoadingSpinner';

/**
 * @component EditEmergencyProfile
 * @description Componente para editar el perfil de emergencia desde el dashboard del usuario
 */
const EditEmergencyProfile: React.FC = () => {
  const { webId } = useParams<{ webId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [profile, setProfile] = useState<WebIDData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cargar datos del perfil
  useEffect(() => {
    const loadProfile = async () => {
      if (!webId || !user) return;

      try {
        setLoading(true);
        setError(null);

        // Obtener todos los WebIDs del usuario y filtrar por el específico
        const userWebIDs = await getWebIDsByUser(user.uid);
        const profileData = userWebIDs.find(profile => profile.id === webId);

        if (!profileData) {
          setError('WebID no encontrado o no tienes permisos para editarlo');
          return;
        }

        setProfile(profileData);
      } catch (err) {
        console.error('Error cargando perfil:', err);
        setError(err instanceof Error ? err.message : 'Error cargando el perfil');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [webId, user]);

  // Convertir datos del perfil al formato del formulario
  const getInitialFormData = (): Partial<EmergencyProfileFormData> => {
    if (!profile?.emergencyProfile) return {};

    const emergencyProfile = profile.emergencyProfile;

    // Mapear contactos de emergencia
    const mappedContacts: EmergencyContact[] = emergencyProfile.emergencyContacts?.map(contact => ({
      id: contact.id,
      name: contact.name,
      relationship: contact.relationship,
      phone: contact.phone,
      email: contact.email || '',
      isPrimary: contact.isPrimary || false,
    })) || [];

    return {
      name: emergencyProfile.fullName || '',
      phone: emergencyProfile.phone?.replace(/^\+\d+\s/, '') || '',
      phoneCountryCode: emergencyProfile.phone?.match(/^\+\d+/)?.[0] || '+56',
      emergencyContacts: mappedContacts,
      bloodType: emergencyProfile.bloodType || '',
      diseases: emergencyProfile.medicalConditions || [],
      allergies: emergencyProfile.allergies || [],
      medications: emergencyProfile.medications || [],
      medicalInfo: emergencyProfile.medicalNotes || '',
      medicalNotes: emergencyProfile.medicalNotes || '',
      isPublic: emergencyProfile.isPublic || false,
      showAllergies: emergencyProfile.showAllergies ?? true,
      showMedicalConditions: emergencyProfile.showMedicalConditions ?? true,
      showMedications: emergencyProfile.showMedications ?? true,
      showMedicalNotes: emergencyProfile.showMedicalNotes ?? true,
      showEmergencyContacts: emergencyProfile.showEmergencyContacts ?? true,
      allowEmergencyAccess: emergencyProfile.allowEmergencyAccess ?? true,
    };
  };

  // Manejar envío del formulario
  const handleSubmit = async (formData: EmergencyProfileFormData) => {
    if (!webId || !user) return;

    try {
      setSaving(true);
      setError(null);

      // Convertir datos del formulario al formato de la base de datos
      const updatedProfile = {
        fullName: formData.name || '',
        phone: `${formData.phoneCountryCode || '+56'} ${formData.phone || ''}`,
        emergencyContacts: formData.emergencyContacts.map(contact => {
          const contactData: any = {
            id: contact.id || `contact_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            name: contact.name || '',
            relationship: contact.relationship || '',
            phone: contact.phone || '',
            isPrimary: contact.isPrimary || false,
            canReceiveAlerts: true,
          };

          // Solo agregar email si tiene valor
          if (contact.email && contact.email.trim()) {
            contactData.email = contact.email;
          }

          return contactData;
        }),
        bloodType: formData.bloodType || '',
        medicalConditions: formData.diseases || [],
        allergies: formData.allergies || [],
        medications: formData.medications || [],
        medicalNotes: formData.medicalNotes || formData.medicalInfo || '',
        isPublic: formData.isPublic || false,
        showAllergies: formData.showAllergies ?? true,
        showMedicalConditions: formData.showMedicalConditions ?? true,
        showMedications: formData.showMedications ?? true,
        showMedicalNotes: formData.showMedicalNotes ?? true,
        showEmergencyContacts: formData.showEmergencyContacts ?? true,
        allowEmergencyAccess: formData.allowEmergencyAccess ?? true,
        language: 'es',
        timezone: 'America/Santiago',
      };

      await updateEmergencyProfile(webId, updatedProfile, user.uid);
      
      // Redirigir de vuelta al dashboard
      navigate('/dashboard', { 
        state: { 
          message: 'Perfil de emergencia actualizado exitosamente',
          type: 'success' 
        }
      });
      
    } catch (err) {
      console.error('Error actualizando perfil:', err);
      setError(err instanceof Error ? err.message : 'Error actualizando el perfil');
    } finally {
      setSaving(false);
    }
  };

  // Manejar cancelación
  const handleCancel = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-sm p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <X className="h-6 w-6 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Error</h3>
            <p className="text-gray-600 mb-6">{error}</p>
            <Button onClick={() => navigate('/dashboard')} variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver al Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-sm p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Perfil no encontrado</h3>
            <p className="text-gray-600 mb-6">No se pudo cargar el perfil solicitado</p>
            <Button onClick={() => navigate('/dashboard')} variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver al Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancel}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Volver
            </Button>
          </div>
          
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Editar Perfil de Emergencia</h1>
            <p className="text-gray-600 mt-2">
              Actualiza la información de tu perfil de emergencia WebID: {webId}
            </p>
          </div>
        </div>

        {/* Formulario */}
        <div className="bg-white rounded-lg shadow-sm">
          <EmergencyProfileFormUnified
            mode="edit"
            webId={webId}
            initialData={getInitialFormData()}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            loading={saving}
            error={error}
            showPrivacySettings={true}
            className="p-6"
          />
        </div>
      </div>
    </div>
  );
};

export default EditEmergencyProfile;
